import React, { useState, useContext, useEffect } from "react";
import { GlobalContext } from "../contexts/GlobalContext";
import api from "../axios/api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import CoverPhoto from "../assets/images/banner/cover.jpg";
import CompanyProfile from "./CompanyProfile";
import {
  FaStar,
  FaPhone,
  FaCheckCircle,
  FaEnvelope,
  FaTree,
  FaGift,
  FaBuilding,
  FaMapMarkerAlt,
  FaLock,
  FaPlus,
  FaShieldAlt,
  FaExclamationCircle,
} from "react-icons/fa";
import * as Yup from "yup";
import {
  Box,
  CircularProgress,
  Tabs,
  Tab,
} from "@mui/material";
import ChangePassword from "./ChangePassword";
import Button from "../components/common/styled components/Button";
import Loader from "../components/common/Loader";
import { keys } from "../config/keys";

const EditProfile = () => {
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState(null);
  const { isLoggedIn, setLoggedIn, setProfilePicture } =
    useContext(GlobalContext);
  const [imagePreview, setImagePreview] = useState(null);
  const [coverImagePreview, setCoverImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [organizations, setOrganizations] = useState([]);
  const [industryTypeOptions, setIndustryTypeOptions] = useState([]);
  const [collectedPoints, setCollectedPoints] = useState(0);
  const [redeemedPoints, setRedeemedPoints] = useState(0);
  const [redeemptionPoints, setRedeepmtionPoints] = useState(0);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState(0);

  // Verification & Add State
  const [verifyingType, setVerifyingType] = useState(null); // 'email' | 'phone' | null
  const [contactInputValue, setContactInputValue] = useState("");
  const [otpValue, setOtpValue] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [contactErrorMsg, setContactErrorMsg] = useState("");


  const validationSchema = Yup.object().shape({
    full_name: Yup.string().required("Name is required"),
    ...(editedUser?.role_code === "corporate" && {
      company_name: Yup.string().required("Company name is required"),
      industry_type_id: Yup.string().required("Industry type is required"),
      website: Yup.string().url("Must be a valid URL").nullable(),
    }),
    ...(editedUser?.role_code === "employee" && {
      organization_id: Yup.string().required("Company is required"),
    }),
  });

  const startOtpTimer = () => {
    setOtpTimer(60);
    const interval = setInterval(() => {
      setOtpTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const fetchIndustryTypes = async () => {
    try {
      const response = await api.get("/api/user/industrytypes");
      setIndustryTypeOptions(response.data[0]);
    } catch (error) {
      console.error("Error fetching Organization Data:", error);
      toast.error("Failed to load organization options. Please try again.");
    }
  };

  const fetchData = async () => {
    try {
      const response = await api.get("/api/user/userdetails");
      if (response.data.isLoggedIn) {
        const userData = response.data.user.user;
        setUser(userData);
        setEditedUser(userData);
        setCollectedPoints(response.data.user.user.collected_points || 0);
        setRedeemedPoints(response.data.user.user.redeemed_points || 0);
        setRedeepmtionPoints(
          response.data.user.user.redeemption_requested_point || 0
        );
        setProfilePicture(userData.userProfilePic);
        setLoggedIn(true);

        if (userData.role_code === "employee") {
          const orgResponse = await api.get("/api/user/organizations");
          setOrganizations(orgResponse.data[0]);
        }
      } else {
        setLoggedIn(false);
      }
    } catch (error) {
      console.error("Error fetching user data", error);
      setLoggedIn(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchIndustryTypes();
    fetchData();
  }, [setLoggedIn, setProfilePicture]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "userEmail" || name === "phoneNumber") return;
    setEditedUser((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSendContactOtp = async (type, targetValue) => {
    setContactErrorMsg("");
    const val = targetValue || contactInputValue;
    if (!val) {
      const msg = `Please enter a valid ${type === 'email' ? 'email address' : 'mobile number'}`;
      setContactErrorMsg(msg);
      toast.error(msg);
      return;
    }
    if (type === 'phone') {
      const clean = val.replace(/[^0-9]/g, '');
      if (clean.length !== 10) {
        const msg = 'Please enter a valid 10-digit mobile number';
        setContactErrorMsg(msg);
        toast.error(msg);
        return;
      }
    } else if (type === 'email') {
      if (!/\S+@\S+\.\S+/.test(val)) {
        const msg = 'Please enter a valid email address';
        setContactErrorMsg(msg);
        toast.error(msg);
        return;
      }
    }

    setSendingOtp(true);
    try {
      const response = await api.post('/api/user/send-contact-otp', {
        type,
        value: val,
      });
      toast.success(response.data.message || 'Verification code sent!');
      setOtpSent(true);
      startOtpTimer();
    } catch (error) {
      console.error("send-contact-otp error:", error);
      const msg = error.response?.data?.message || 'Failed to send verification code';
      setContactErrorMsg(msg);
      toast.error(msg);
    } finally {
      setSendingOtp(false);
    }
  };

  const handleVerifyContactOtp = async (type, targetValue) => {
    setContactErrorMsg("");
    const val = targetValue || contactInputValue;
    if (!otpValue || otpValue.length < 6) {
      const msg = 'Please enter the 6-digit verification code';
      setContactErrorMsg(msg);
      toast.error(msg);
      return;
    }

    setVerifyingOtp(true);
    try {
      const response = await api.post('/api/user/verify-contact-otp', {
        type,
        value: val,
        otp: otpValue,
      });
      toast.success(response.data.message || 'Verified successfully!');
      
      await fetchData();
      
      setVerifyingType(null);
      setContactInputValue('');
      setOtpValue('');
      setOtpSent(false);
    } catch (error) {
      console.error("verify-contact-otp error:", error);
      const msg = error.response?.data?.message || 'Failed to verify code';
      setContactErrorMsg(msg);
      toast.error(msg);
    } finally {
      setVerifyingOtp(false);
    }
  };

  const handleSave = async () => {
    try {
      setSubmitting(true);
      await validationSchema.validate(editedUser, { abortEarly: false });

      const token = localStorage.getItem("token");
      const formData = new FormData();
      formData.append("userName", editedUser.full_name);

      if (editedUser.userProfilePic instanceof File) {
        formData.append("profilePicture", editedUser.userProfilePic);
      }
      if (editedUser.userCoverPic instanceof File) {
        formData.append("coverImage", editedUser.userCoverPic);
      }
      if (editedUser.role_code === "employee") {
        formData.append("organizationId", editedUser.organization_id);
      }

      const response = await api.put(
        `/api/user/update/${user.id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.success) {
        setUser({ ...editedUser });
        setIsEditing(false);
        toast.success("Profile updated successfully");
        await fetchData();
      } else {
        console.error("Error updating profile:", response.data.message);
        toast.error("Failed to update profile. Please try again.");
      }
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const newErrors = {};
        error.inner.forEach((err) => {
          newErrors[err.path] = err.message;
        });
        setErrors(newErrors);
      } else {
        console.error("Error updating profile:", error);
        toast.error("An error occurred while updating your profile.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = () => setIsEditing(true);
  
  const handleCancel = () => {
    setEditedUser({ ...user });
    setIsEditing(false);
    setImagePreview(null);
    setCoverImagePreview(null);
  };

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditedUser((prev) => ({ ...prev, userProfilePic: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleCoverImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setEditedUser((prev) => ({ ...prev, userCoverPic: file }));
      setCoverImagePreview(URL.createObjectURL(file));
    }
  };

  const handleChangeTab = (event, newValue) => {
    setActiveTab(newValue);
  };

  if (!isLoggedIn || !user) {
    return <div className="text-center text-lg text-gray-600 p-10">You are not logged in.</div>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      {(user.role_code === "corporate" || user.role_code === 'ngo') ? (
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          centered
          sx={{ marginBottom: 2 }}
        >
          <Tab
            label="User Profile"
            sx={{
              color: 'black',
              minHeight: '48px',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              paddingX: 3,
              cursor: 'pointer',
              '&.Mui-selected': {
                backgroundColor: '#4caf50 !important',
                color: 'white !important',
              },
              '&:hover': {
                color: 'white !important',
                backgroundColor: '#4caf50',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'scale(1.03)',
              },
            }}
          />
          <Tab
            label="Company Profile"
            sx={{
              color: 'black',
              minHeight: '48px',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              paddingX: 3,
              cursor: 'pointer',
              '&.Mui-selected': {
                backgroundColor: '#4caf50 !important',
                color: 'white !important',
              },
              '&:hover': {
                color: 'white !important',
                backgroundColor: '#4caf50',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'scale(1.03)',
              },
            }}
          />
          <Tab
            label="Change Password"
            sx={{
              color: 'black',
              minHeight: '48px',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              paddingX: 3,
              cursor: 'pointer',
              '&.Mui-selected': {
                backgroundColor: '#4caf50 !important',
                color: 'white !important',
              },
              '&:hover': {
                color: 'white !important',
                backgroundColor: '#4caf50',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'scale(1.03)',
              },
            }}
          />
        </Tabs>
      ) : (
        <Tabs
          value={activeTab}
          onChange={handleChangeTab}
          centered
          sx={{
            marginBottom: 2,
            '& .MuiTabs-indicator': {
              display: 'none',
            },
          }}
        >
          <Tab
            label="User Profile"
            sx={{
              color: 'black',
              minHeight: '48px',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              paddingX: 3,
              cursor: 'pointer',
              borderRadius: 0,
              '&.Mui-selected': {
                backgroundColor: '#4caf50 !important',
                color: 'white !important',
              },
              '&:hover': {
                color: 'white !important',
                backgroundColor: '#4caf50',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'scale(1.03)',
              },
            }}
          />
          <Tab
            label="Change Password"
            sx={{
              color: 'black',
              minHeight: '48px',
              fontWeight: 600,
              textTransform: 'none',
              transition: 'all 0.3s ease',
              paddingX: 3,
              cursor: 'pointer',
              borderRadius: 0,
              '&.Mui-selected': {
                backgroundColor: '#4caf50 !important',
                color: 'white !important',
              },
              '&:hover': {
                color: 'white !important',
                backgroundColor: '#4caf50',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                transform: 'scale(1.03)',
              },
            }}
          />
        </Tabs>
      )}

      {activeTab === 0 ? (
        <div className="mx-auto w-full max-w-2xl bg-white rounded-lg shadow-md overflow-hidden">
          {/* Cover Photo */}
          <div className="relative h-32 md:h-40">
            {isEditing ? (
              <label htmlFor="coverPhotoInput" className="relative block h-32 md:h-40 cursor-pointer">
                <img
                  src={coverImagePreview || user.userCoverPic || CoverPhoto}
                  alt="Cover"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-transparent hover:bg-black/20 transition-colors duration-300" />
                <div className="absolute bottom-0 left-0 right-0 bg-black/40 text-white text-sm md:text-base text-center p-2 z-10">
                  Edit Cover Photo
                </div>
                <input
                  type="file"
                  accept="image/*"
                  id="coverPhotoInput"
                  onChange={handleCoverImageChange}
                  className="hidden"
                />
              </label>
            ) : (
              <div className="h-32 md:h-40">
                <img
                  src={user.userCoverPic || CoverPhoto}
                  alt="Cover"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          <div className="px-4 py-3 md:px-6 md:py-5">
            {/* Profile Header */}
            <div className="flex flex-col items-center mb-4 md:mb-6">
              <div className="relative w-24 h-24 md:w-28 md:h-28 -mt-12 mb-3">
                {isEditing ? (
                  <label htmlFor="profilePictureInput" className="relative block w-full h-full cursor-pointer">
                    <img
                      src={imagePreview || user.userProfilePic || "/images/sprout.png"}
                      alt="Profile"
                      className="w-full h-full rounded-full object-cover border-4 border-white shadow-sm"
                    />
                    <div className="absolute inset-0 rounded-full bg-transparent hover:bg-black/30 transition-colors duration-300" />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs text-center px-2 py-1 rounded-b-full z-10">
                      Edit Profile Picture
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      id="profilePictureInput"
                      onChange={handleProfilePictureChange}
                      className="hidden"
                    />
                  </label>
                ) : (
                  <img
                    src={user.userProfilePic || "/images/sprout.png"}
                    alt="Profile"
                    className="w-full h-full rounded-full object-cover border-4 border-white shadow-sm"
                  />
                )}
              </div>

              {/* Name and Wallet */}
              <div className="text-center w-full">
                <h2 className="text-xl font-bold text-gray-800 mb-1">{user.full_name}</h2>
                <p className="text-sm text-gray-600 mb-2">Role: {user.role_name}</p>

                <div className="flex items-center justify-center space-x-1 text-sm">
                  <span className="text-gray-700">Current Balance:</span>
                  <span className="font-semibold text-gray-800">
                    {collectedPoints > 9000
                      ? `${Math.floor(collectedPoints / 1000)}K`
                      : collectedPoints}
                  </span>
                  <FaStar className="text-green-600 text-sm" />
                </div>
              </div>
            </div>

            {/* Profile Details Form */}
            <div className="bg-gray-50 rounded-lg p-4 mb-4">
              {isEditing ? (
                <div className="space-y-3">
                  <p className="text-xs text-gray-500">
                    NOTE: fields marked with <span className="text-red-500">*</span> are mandatory to input.
                  </p>

                  <div className="space-y-1">
                    <label htmlFor="userName" className="block text-sm font-medium text-gray-700">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      value={editedUser.full_name}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 text-base border rounded-md focus:outline-none focus:ring-2 ${
                        errors.full_name ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-green-500"
                      }`}
                    />
                    {errors.full_name && (
                      <span className="text-xs text-red-500">{errors.full_name}</span>
                    )}
                  </div>

                  {user.role_code === "employee" && (
                    <div className="space-y-1">
                      <label htmlFor="organization" className="block text-sm font-medium text-gray-700">Company</label>
                      <select
                        name="organization_id"
                        value={editedUser.organization_id}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 text-base border rounded-md focus:outline-none focus:ring-2 ${
                          errors.organization_id ? "border-red-500 focus:ring-red-500" : "border-gray-300 focus:ring-green-500"
                        }`}
                      >
                        <option value="">Select Company</option>
                        {organizations.map((org) => (
                          <option key={org.id} value={org.id}>
                            {org.company_name}
                          </option>
                        ))}
                      </select>
                      {errors.organization_id && (
                        <span className="text-xs text-red-500">{errors.organization_id}</span>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="flex flex-col space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 sm:gap-4">
                    {user.role_code === "employee" && (
                      <div className="flex items-center space-x-2 col-span-2">
                        <FaBuilding className="text-gray-600 text-sm flex-shrink-0" />
                        <span className="text-sm font-medium text-gray-700">Company:</span>
                        <span className="text-sm text-gray-600">{user.company_name || ""}</span>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      <FaTree className="text-gray-600 text-sm flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700">Total Trees:</span>
                      <span className="text-sm text-gray-600">{user.totalTrees}</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <FaCheckCircle className="text-green-600 text-sm flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700">Verified Trees:</span>
                      <span className="text-sm text-gray-600">{user.verifiedTrees}</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <FaMapMarkerAlt className="text-gray-600 text-sm flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700">Geotagged Trees:</span>
                      <span className="text-sm text-gray-600">{user.geoLocatedTreesCount}</span>
                    </div>

                    <div className="flex items-center space-x-2">
                      <FaGift className="text-gray-600 text-sm flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700">Rewarded points:</span>
                      <span className="text-sm text-gray-600">{redeemedPoints}</span>
                    </div>

                    <div className="flex items-center space-x-2 col-span-2">
                      <FaStar className="text-gray-600 text-sm flex-shrink-0" />
                      <span className="text-sm font-medium text-gray-700">Redemption Requested points:</span>
                      <span className="text-sm text-gray-600">{redeemptionPoints}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* CONTACT & VERIFICATION SECTION */}
            <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4 shadow-sm">
              <div className="flex items-center justify-between border-b pb-2 mb-3">
                <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                  <FaShieldAlt className="text-green-600" /> Contact Details & Verification
                </h3>
                <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded border">
                  At least 1 contact required
                </span>
              </div>

              <p className="text-xs text-gray-500 mb-3 bg-amber-50 border-l-2 border-amber-400 p-2 rounded">
                <strong>Note:</strong> Existing email and mobile numbers cannot be edited. You can only add a missing contact method after completing 6-digit verification code validation.
              </p>

              <div className="space-y-3">
                {/* EMAIL ROW */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gray-50 rounded-md border gap-2">
                  <div className="flex items-center space-x-3">
                    <FaEnvelope className="text-gray-500 text-lg flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Email Address</p>
                      {user.userEmail ? (
                        <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                          {user.userEmail}
                          <FaLock className="text-gray-400 text-xs" title="Existing email cannot be edited" />
                        </p>
                      ) : (
                        <p className="text-xs text-amber-600 italic">No Email Address Linked</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {user.userEmail ? (
                      user.isVerified == 1 || user.isVerified === true ? (
                        <span className="bg-green-100 text-green-700 text-xs px-2.5 py-1 rounded-full font-semibold flex items-center gap-1">
                          <FaCheckCircle className="text-green-600" /> Verified
                        </span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="bg-amber-100 text-amber-700 text-xs px-2.5 py-1 rounded-full font-semibold">
                            Unverified
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setVerifyingType('email');
                              setContactInputValue(user.userEmail);
                              setOtpSent(false);
                              setOtpValue('');
                              setContactErrorMsg('');
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 rounded-md transition font-medium"
                          >
                            Verify Email
                          </button>
                        </div>
                      )
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setVerifyingType('email');
                          setContactInputValue('');
                          setOtpSent(false);
                          setOtpValue('');
                          setContactErrorMsg('');
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1.5 rounded-md flex items-center gap-1 font-medium transition"
                      >
                        <FaPlus size={10} /> Add Email
                      </button>
                    )}
                  </div>
                </div>

                {/* PHONE ROW */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between p-3 bg-gray-50 rounded-md border gap-2">
                  <div className="flex items-center space-x-3">
                    <FaPhone className="text-gray-500 text-lg flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500 font-medium">Mobile Number</p>
                      {user.phoneNumber ? (
                        <p className="text-sm font-semibold text-gray-800 flex items-center gap-1.5">
                          +91 {user.phoneNumber}
                          <FaLock className="text-gray-400 text-xs" title="Existing mobile number cannot be edited" />
                        </p>
                      ) : (
                        <p className="text-xs text-amber-600 italic">No Mobile Number Linked</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {user.phoneNumber ? (
                      user.is_phone_verified == 1 || user.is_phone_verified === true ? (
                        <span className="bg-green-100 text-green-700 text-xs px-2.5 py-1 rounded-full font-semibold flex items-center gap-1">
                          <FaCheckCircle className="text-green-600" /> Verified
                        </span>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="bg-amber-100 text-amber-700 text-xs px-2.5 py-1 rounded-full font-semibold">
                            Unverified
                          </span>
                          <button
                            type="button"
                            onClick={() => {
                              setVerifyingType('phone');
                              setContactInputValue(user.phoneNumber);
                              setOtpSent(false);
                              setOtpValue('');
                              setContactErrorMsg('');
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1 rounded-md transition font-medium"
                          >
                            Verify Mobile
                          </button>
                        </div>
                      )
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setVerifyingType('phone');
                          setContactInputValue('');
                          setOtpSent(false);
                          setOtpValue('');
                          setContactErrorMsg('');
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white text-xs px-3 py-1.5 rounded-md flex items-center gap-1 font-medium transition"
                      >
                        <FaPlus size={10} /> Add Mobile
                      </button>
                    )}
                  </div>
                </div>

                {/* INLINE OTP VERIFICATION / ADD FORM */}
                {verifyingType && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg space-y-3">
                    <div className="flex justify-between items-center">
                      <h4 className="text-sm font-semibold text-green-800 flex items-center gap-1.5">
                        <FaShieldAlt className="text-green-600" />
                        {user[verifyingType === 'email' ? 'userEmail' : 'phoneNumber']
                          ? `Verify ${verifyingType === 'email' ? 'Email Address' : 'Mobile Number'}`
                          : `Add & Verify New ${verifyingType === 'email' ? 'Email Address' : 'Mobile Number'}`}
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          setVerifyingType(null);
                          setOtpSent(false);
                          setOtpValue('');
                          setContactErrorMsg('');
                        }}
                        className="text-xs text-gray-500 hover:text-gray-700 underline"
                      >
                        Cancel
                      </button>
                    </div>

                    {contactErrorMsg && (
                      <div className="p-2.5 bg-red-50 border border-red-300 text-red-700 rounded-md text-xs font-medium flex items-center gap-1.5">
                        <FaExclamationCircle className="text-red-500 text-sm flex-shrink-0" />
                        <span>{contactErrorMsg}</span>
                      </div>
                    )}

                    {!user[verifyingType === 'email' ? 'userEmail' : 'phoneNumber'] && (
                      <div className="space-y-1">
                        <label className="block text-xs font-medium text-gray-700">
                          Enter {verifyingType === 'email' ? 'Email Address' : '10-Digit Mobile Number'}
                        </label>
                        <input
                          type={verifyingType === 'email' ? 'email' : 'text'}
                          disabled={otpSent}
                          value={contactInputValue}
                          onChange={(e) => setContactInputValue(e.target.value)}
                          placeholder={verifyingType === 'email' ? 'example@company.com' : '9876543210'}
                          className="w-full px-3 py-2 text-sm border rounded-md focus:ring-2 focus:ring-green-500 bg-white"
                        />
                      </div>
                    )}

                    {!otpSent ? (
                      <button
                        type="button"
                        disabled={sendingOtp || !contactInputValue.trim()}
                        onClick={() => handleSendContactOtp(verifyingType, contactInputValue)}
                        className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white py-2 rounded-md text-sm font-semibold transition flex justify-center items-center gap-2"
                      >
                        {sendingOtp ? <CircularProgress size={16} color="inherit" /> : 'Send Verification Code'}
                      </button>
                    ) : (
                      <div className="space-y-3 pt-2">
                        <div className="p-2 bg-white rounded border border-green-300 text-xs text-green-700">
                          Verification code sent to <strong>{contactInputValue}</strong>.
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Enter 6-Digit Verification Code
                          </label>
                          <input
                            type="text"
                            maxLength={6}
                            value={otpValue}
                            onChange={(e) => setOtpValue(e.target.value.trim())}
                            placeholder="123456"
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500 bg-white letter-spacing-2 font-mono text-center text-lg"
                          />
                        </div>

                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={verifyingOtp || otpValue.length < 6}
                            onClick={() => handleVerifyContactOtp(verifyingType, contactInputValue)}
                            className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white py-2 rounded-md text-sm font-semibold transition flex justify-center items-center gap-2"
                          >
                            {verifyingOtp ? <CircularProgress size={16} color="inherit" /> : 'Verify & Confirm'}
                          </button>

                          <button
                            type="button"
                            disabled={sendingOtp || otpTimer > 0}
                            onClick={() => handleSendContactOtp(verifyingType, contactInputValue)}
                            className="px-3 py-2 text-xs border border-gray-300 hover:bg-gray-100 rounded-md text-gray-700 transition"
                          >
                            {otpTimer > 0 ? `Resend in ${otpTimer}s` : 'Resend Code'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center md:justify-end space-x-3">
              {isEditing ? (
                <>
                  <Button
                    onClick={handleSave}
                    disabled={submitting}
                  >
                    {submitting ? <CircularProgress size={20} color="inherit" /> : "Save"}
                  </Button>
                  <Button
                    variant="cancel"
                    onClick={handleCancel}
                    disabled={submitting}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleEdit}
                >
                  Edit Profile
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : activeTab === 1 && (user.role_code === "corporate" || user.role_code === 'ngo') ? (
        <CompanyProfile />
      ) : (
        <ChangePassword />
      )}
      <ToastContainer position="top-right" autoClose={4000} />
    </Box>
  );
};

export default EditProfile;
